import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { X, Phone, Clock, Play, Calendar, AlertCircle } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { parseISO, isValid } from 'date-fns';
import { formatInTz } from '../../utils/dateFormatters';
import { useTimezone } from '../../hooks/useTimezone';

interface CallRecordingsModalProps {
  leadId: string;
  leadName: string;
  onClose: () => void;
}

const CallRecordingsModal: React.FC<CallRecordingsModalProps> = ({ leadId, leadName, onClose }) => {
  const tz = useTimezone();
  const [callLogs, setCallLogs] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchCallLogs = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const { data: logs, error: fetchError } = await supabase
          .from('call_logs')
          .select('*')
          .eq('lead_id', leadId)
          .order('created_at', { ascending: false });

        if (fetchError) throw fetchError;

        setCallLogs(logs || []);
      } catch (err) {
        console.error('[CallRecordingsModal] Failed to fetch call logs:', err);
        setError('Failed to load call recordings');
      } finally {
        setIsLoading(false);
      }
    };

    if (leadId) {
      fetchCallLogs();
    }
  }, [leadId]);

  const formatDateTime = (timestamp: string) => {
    if (!timestamp || timestamp === 'N/A') return 'Unknown time';
    try {
      const date = parseISO(timestamp);
      if (!isValid(date)) return 'Invalid date';
      return formatInTz(date, 'MMM dd, yyyy - HH:mm', tz);
    } catch {
      return 'Invalid date';
    }
  };

  const getCallOutcome = (outcome: string) => {
    if (!outcome || outcome === 'N/A') return null;

    const outcomeColors: { [key: string]: string } = {
      'connected': 'bg-[#05E0D1]/10 text-[#0bb5a5] border-[#05E0D1]/25',
      'not connected': 'bg-rose-500/10 text-rose-700 border-rose-500/20',
      'voicemail': 'bg-amber-500/10 text-amber-700 border-amber-500/20',
      'busy': 'bg-[#1875FF]/8 text-[#1875FF] border-[#1875FF]/20',
      'no answer': 'bg-gray-100 text-gray-600 border-gray-200'
    };

    const colorClass = outcomeColors[outcome.toLowerCase()] || 'bg-gray-100 text-gray-600 border-gray-200';

    return (
      <span className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-semibold border ${colorClass}`}>
        {outcome}
      </span>
    );
  };

  return ReactDOM.createPortal(
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[85vh] overflow-hidden flex flex-col shadow-2xl shadow-black/20 animate-fadeIn">
        <div className="flex items-center justify-between px-6 py-5 border-b border-[#05E0D1]/20 bg-gradient-to-r from-[#1875FF]/5 to-[#05E0D1]/5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-[#1875FF] to-[#05E0D1] rounded-xl flex items-center justify-center shadow-lg">
              <Phone className="text-white" size={18} />
            </div>
            <div>
              <h3 className="text-lg font-extrabold text-gray-900 font-display">Call Recordings</h3>
              <p className="text-xs text-gray-500 font-medium">Recordings for {leadName}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-white rounded-lg"
          >
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto custom-scrollbar p-6">
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <div className="w-8 h-8 border-2 border-[#1875FF] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                <div className="text-gray-400 text-sm font-medium">Loading call recordings...</div>
              </div>
            </div>
          ) : error ? (
            <div className="text-center py-16">
              <div className="w-12 h-12 bg-gradient-to-br from-rose-500/20 to-rose-600/20 rounded-xl flex items-center justify-center mx-auto mb-3">
                <AlertCircle size={24} className="text-rose-500" />
              </div>
              <p className="text-gray-500 text-sm font-medium">{error}</p>
            </div>
          ) : callLogs.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-12 h-12 bg-gradient-to-br from-[#1875FF]/10 to-[#05E0D1]/10 rounded-xl flex items-center justify-center mx-auto mb-3">
                <Phone size={24} className="text-[#1875FF]" />
              </div>
              <p className="text-gray-500 text-sm font-medium">No call recordings found</p>
              <p className="text-gray-400 text-xs mt-1">Recordings will appear here once available</p>
            </div>
          ) : (
            <div className="space-y-6">
              <div className="flex items-center gap-2 mb-2">
                <Calendar className="text-[#1875FF]" size={18} />
                <span className="text-sm font-bold text-gray-900">
                  Call Timeline ({callLogs.length} recording{callLogs.length !== 1 ? 's' : ''})
                </span>
              </div>

              <div className="relative">
                <div className="absolute left-5 top-0 bottom-0 w-0.5 bg-gradient-to-b from-[#1875FF]/20 via-[#05E0D1]/15 to-transparent"></div>

                {callLogs.map((callLog, index) => {
                  const hasRecording = callLog.call_recording && callLog.call_recording !== 'N/A';

                  return (
                    <div key={index} className="relative flex items-start gap-4 pb-6">
                      <div className="relative z-10 w-10 h-10 bg-gradient-to-br from-[#1875FF] to-[#05E0D1] rounded-xl flex items-center justify-center shadow-md flex-shrink-0">
                        <Phone className="text-white" size={14} />
                      </div>

                      {!hasRecording ? (
                        <div className="flex-1 bg-white rounded-xl border border-gray-200 shadow-sm px-4 py-3 flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Clock className="text-gray-400" size={14} />
                            <span className="text-sm font-semibold text-gray-900">
                              {formatDateTime(callLog.created_at)}
                            </span>
                          </div>
                          {callLog.call_outcome && getCallOutcome(callLog.call_outcome)}
                        </div>
                      ) : (
                        <div className="flex-1 bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                          <div className="p-4">
                            <div className="flex items-center justify-between mb-3">
                              <div className="flex items-center gap-2">
                                <Clock className="text-gray-400" size={14} />
                                <span className="text-sm font-semibold text-gray-900">
                                  {formatDateTime(callLog.created_at)}
                                </span>
                              </div>
                              {callLog.call_outcome && getCallOutcome(callLog.call_outcome)}
                            </div>

                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                              {callLog.call_duration && (
                                <div className="bg-gray-50 rounded-lg px-3 py-2">
                                  <span className="text-[10px] text-gray-400 font-medium uppercase tracking-widest">Duration</span>
                                  <div className="text-sm font-semibold text-gray-900">{callLog.call_duration}</div>
                                </div>
                              )}
                              {callLog.phone_number && (
                                <div className="bg-gray-50 rounded-lg px-3 py-2">
                                  <span className="text-[10px] text-gray-400 font-medium uppercase tracking-widest">Phone</span>
                                  <div className="text-sm font-semibold text-gray-900">{callLog.phone_number}</div>
                                </div>
                              )}
                              {callLog.call_type && (
                                <div className="bg-gray-50 rounded-lg px-3 py-2">
                                  <span className="text-[10px] text-gray-400 font-medium uppercase tracking-widest">Type</span>
                                  <div className="text-sm font-semibold text-gray-900">{callLog.call_type}</div>
                                </div>
                              )}
                              {callLog.agent_name && (
                                <div className="bg-gray-50 rounded-lg px-3 py-2">
                                  <span className="text-[10px] text-gray-400 font-medium uppercase tracking-widest">Agent</span>
                                  <div className="text-sm font-semibold text-gray-900">{callLog.agent_name}</div>
                                </div>
                              )}
                            </div>

                            <div className="bg-gradient-to-r from-[#1875FF]/5 to-[#05E0D1]/5 rounded-xl p-4 border border-[#05E0D1]/15">
                              <div className="flex items-center gap-2 mb-3">
                                <div className="w-6 h-6 bg-gradient-to-br from-[#05E0D1] to-[#1875FF] rounded-lg flex items-center justify-center">
                                  <Play className="text-white" size={10} />
                                </div>
                                <span className="text-xs font-semibold text-gray-700">Call Recording</span>
                              </div>
                              <audio
                                controls
                                className="w-full"
                                preload="metadata"
                                key={`${callLog.lead_id}-${index}`}
                              >
                                <source src={callLog.call_recording} type="audio/mpeg" />
                                <source src={callLog.call_recording} type="audio/wav" />
                                <source src={callLog.call_recording} type="audio/mp3" />
                                Your browser does not support the audio element.
                              </audio>
                            </div>

                            {callLog.call_notes && callLog.call_notes !== 'N/A' && (
                              <div className="mt-3 bg-[#05E0D1]/5 rounded-xl p-3 border border-[#05E0D1]/15">
                                <div className="text-[10px] text-[#0bb5a5] font-semibold uppercase tracking-widest mb-1">Call Notes</div>
                                <div className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">{callLog.call_notes}</div>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <div className="px-6 py-3 border-t border-gray-100 bg-white">
          <p className="text-xs text-gray-400 text-center font-medium">
            {callLogs.length} recording{callLogs.length !== 1 ? 's' : ''} found
          </p>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default CallRecordingsModal;
